import React, { useState, useEffect } from 'react';
import { IoInformationCircle } from 'react-icons/io5';
import { TbListDetails } from 'react-icons/tb';
import Select from 'react-select';
import { FaPencilAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

export default function AddUserAnnouncements() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    message: '',
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('success');

  // Fetch users for the multi-select dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch('https://backend.ems.hiradentallab.com/api/user/getAllUsers');
        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }
        const data = await response.json();
        setUsers(data);
      } catch (error) {
        console.error('Error fetching users:', error);
        setAlertMessage('Failed to fetch users');
        setAlertVariant('danger');
        setShowAlert(true);
      }
    };
    fetchUsers();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleUserSelectChange = (selected) => {
    setSelectedUsers(selected || []);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
      return validTypes.includes(file.type);
    });

    if (validFiles.length !== files.length) {
      setAlertMessage('Only PNG, JPG, JPEG, and PDF files are allowed.');
      setAlertVariant('danger');
      setShowAlert(true);
    }

    setSelectedFiles(validFiles);
  };

  const groupedOptions = [
    { label: 'Admin', options: users.filter((u) => u.role === 'Admin' && u.status === 'active').map((u) => ({ value: u.userId, label: u.name })) },
    { label: 'HR', options: users.filter((u) => u.role === 'HR' && u.status === 'active').map((u) => ({ value: u.userId, label: u.name })) },
    { label: 'Accounts', options: users.filter((u) => u.role === 'Accounts' && u.status === 'active').map((u) => ({ value: u.userId, label: u.name })) },
    { label: 'Employee', options: users.filter((u) => u.role === 'Employee' && u.status === 'active').map((u) => ({ value: u.userId, label: u.name })) },
  ].filter((group) => group.options.length > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const author = localStorage.getItem('role');
    if (!author) {
      setAlertMessage('Author role is missing. Please log in again.');
      setAlertVariant('danger');
      setShowAlert(true);
      return;
    }

    if (!formData.title || !formData.message) {
      setAlertMessage('Please fill in all required fields.');
      setAlertVariant('danger');
      setShowAlert(true);
      return;
    }

    if (selectedUsers.length === 0) {
      setAlertMessage('Please select at least one user.');
      setAlertVariant('danger');
      setShowAlert(true);
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('message', formData.message);
    formDataToSend.append('author', author);
    formDataToSend.append(
      'users',
      JSON.stringify(selectedUsers.map((user) => ({
        userId: user.value,
        name: user.label,
      })))
    );
    selectedFiles.forEach((file) => {
      formDataToSend.append('files', file);
    });

    try {
      const response = await fetch('https://backend.ems.hiradentallab.com/api/user/announcements', {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to add announcement');
      }

      setAlertMessage('✅ Announcement added successfully!');
      setAlertVariant('success');
      setShowAlert(true);

      setFormData({ title: '', message: '' });
      setSelectedUsers([]);
      setSelectedFiles([]);

      // Delay navigation for 2s so user sees the alert
      setTimeout(() => {
        setShowAlert(false);
        navigate('/user-announcements');
      }, 2000);
    } catch (error) {
      console.error('Error details:', error);
      setAlertMessage(error.message || 'Failed to add announcement');
      setAlertVariant('danger');
      setShowAlert(true);
    }
  };

  return (
    <div className="container-fluid">
      <h1 style={{ marginTop: '60px', fontSize: '2rem', color: '#0353a4' }}>
        <IoInformationCircle className="salary-icon" /> Announcements Information
      </h1>
      <hr />
      <h2 className="salary-subtitle">
        <TbListDetails className="salary-icon" /> Announcements Details
      </h2>
      <hr />
      <div className="table-container">
        {showAlert && (
          <div
            className={`alert alert-${alertVariant}`}
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              zIndex: 9999,
              minWidth: '250px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              borderRadius: '8px',
              fontWeight: '500',
              animation: 'fadeIn 0.3s ease-in-out',
            }}
          >
            {alertMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="title" style={{ fontWeight: 'bold' }}>
              Announcement Title <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              className="form-control"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="message" style={{ fontWeight: 'bold' }}>
              Announcement Message <span style={{ color: 'red' }}>*</span>
            </label>
            <textarea
              id="message"
              name="message"
              className="form-control"
              value={formData.message}
              onChange={handleChange}
              required
            ></textarea>
          </div>
          <div className="form-group">
            <label htmlFor="users" style={{ fontWeight: 'bold' }}>
              Select Users <span style={{ color: 'red' }}>*</span>
            </label>
            <Select
              id="users"
              isMulti
              isSearchable
              options={groupedOptions}
              onChange={handleUserSelectChange}
              value={selectedUsers}
              placeholder="Select users..."
            />
          </div>
          <div className="form-group">
            <label htmlFor="files" style={{ fontWeight: 'bold' }}>
              Upload Files (PNG, JPG, JPEG, PDF) (Optional)
            </label>
            <input
              type="file"
              id="files"
              name="files"
              className="form-control"
              accept="image/png,image/jpeg,image/jpg,application/pdf"
              multiple
              onChange={handleFileChange}
            />
            {selectedFiles.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <p style={{ fontWeight: '500' }}>Selected Files:</p>
                <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem' }}>
                  {selectedFiles.map((file, index) => (
                    <li key={index} style={{ fontSize: '0.875rem' }}>{file.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="py-3">
            <button type="submit" className="btn btn-primary">
              <FaPencilAlt /> Add Announcement
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
